import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PartnersManager = () => {
  const [partners, setPartners] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    logo_url: '',
    referral_link: '',
    category: 'hotels'
  });

  const categories = ['hotels', 'airlines', 'tours', 'insurance', 'gear', 'other'];

  const fetchPartners = async () => {
    try {
      const response = await axios.get(`${API}/partners`);
      setPartners(response.data);
    } catch (error) {
      toast.error('Failed to fetch partners');
    }
  };

  useEffect(() => {
    fetchPartners();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    try {
      await axios.post(`${API}/partners`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Partner added successfully');
      setIsDialogOpen(false);
      setFormData({
        name: '',
        description: '',
        logo_url: '',
        referral_link: '',
        category: 'hotels'
      });
      fetchPartners();
    } catch (error) {
      toast.error('Failed to add partner');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this partner?')) return;
    
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/partners/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Partner deleted');
      fetchPartners();
    } catch (error) {
      toast.error('Failed to delete partner');
    }
  };

  return (
    <div data-testid="partners-manager">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-3xl font-heading font-bold text-maroon">Partners</h2>
        <Button onClick={() => setIsDialogOpen(true)} className="btn-primary" data-testid="add-partner-btn">
          <Plus size={20} className="mr-2" />
          Add Partner
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {partners.map((partner) => (
          <div key={partner.id} className="card" data-testid="partner-item">
            <div className="h-32 flex items-center justify-center bg-white border border-maroon/10 rounded-sm mb-3 p-4">
              <img src={partner.logo_url} alt={partner.name} className="max-h-full max-w-full object-contain" />
            </div>
            <h3 className="font-heading font-bold text-lg text-maroon mb-2">{partner.name}</h3>
            <p className="text-maroon/70 text-sm mb-3 line-clamp-2">{partner.description}</p>
            <div className="flex items-center justify-between">
              <span className="text-xs bg-terracotta/10 text-terracotta px-2 py-1 rounded-sm font-semibold capitalize">
                {partner.category}
              </span>
              <Button 
                onClick={() => handleDelete(partner.id)} 
                variant="destructive" 
                size="sm"
                data-testid="delete-partner-btn"
              >
                <Trash2 size={16} />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-maroon">Add Partner</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="partner-form">
            <div className="space-y-2">
              <Label htmlFor="name">Partner Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="input-field"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                rows={3}
                className="bg-white border-2 border-maroon/20 focus:border-maroon focus:ring-0 rounded-sm resize-none"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="logo_url">Logo URL *</Label>
              <Input
                id="logo_url"
                type="url"
                value={formData.logo_url}
                onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                required
                className="input-field"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="referral_link">Referral Link *</Label>
              <Input
                id="referral_link"
                type="url"
                value={formData.referral_link}
                onChange={(e) => setFormData({ ...formData, referral_link: e.target.value })}
                required
                className="input-field"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">Category *</Label>
              <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                <SelectTrigger className="input-field">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat} className="capitalize">{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button type="submit" className="btn-primary w-full">
              Add Partner
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartnersManager;